import { Topbar } from '../../components/Topbar/Topbar';
import { Alert, Badge, Button, Card, Col, Container, Form, InputGroup, ListGroup, Modal, Row, Tab, Tabs } from 'react-bootstrap';
import { Footer } from '../../components/Footer/Footer';
import { useEffect, useState } from 'react';
import { FullscreenLoader } from '../../components/FullscreenLoader/FullscreenLoader';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBookMedical,
  faCertificate,
  faExclamation,
  faGraduationCap,
  faHandHoldingMedical,
  faHandshake,
  faPlus,
  faQuestionCircle,
  faQuoteRight,
  faTag,
  faTags,
  faTooth,
  faVideo,
} from '@fortawesome/free-solid-svg-icons';
import { AdminVideo } from './AdminVideo';
import { AdminTag } from './AdminTag';
import { AdminAuthor } from './AdminAuthor';
import { AdminCreateTag } from './AdminCreateTag';
import { AdminCreateAuthor } from './AdminCreateAuthor';
import { Wip } from '../../components/Wip/Wip';
import { WithTooltip } from '../../components/WithTooltip/WithTooltip';
import { Title } from '../../components/Title/Title';
import styles from './Content.module.scss';
import { Copyable } from '../../components/Copyable/Copyable';
import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import { CountryFlag } from '../../components/CountryFlag/CountryFlag';
import { EditCourse } from '../../components/EditCourse/EditCourse';
import { SeoUpdateInProgress } from '../../components/SeoUpdateInProgress/SeoUpdateInProgress';
import { database } from '../../../firebase';
import { getPublicResourcesBaseUrl } from '../../utils/utils';

const ContentComponent = () => {
  const { upsertHubspotProduct, manageCache, requestVideoContent, requestCourseContent } = useFirebase();

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('help');

  const [videos, setVideos] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);

  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [coursesFilter, setCoursesFilter] = useState('');
  const [editCourseModal, setEditCourseModal] = useState(false);
  const [isCourseReadOnly, setIsCourseReadOnly] = useState(false);
  const [courseId, setCourseId] = useState<string>(null);
  const [course, setCourse] = useState(null);

  const [tags, setTags] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [faqs, setFaqs] = useState([]);

  const [videoEditId, setVideoEditId] = useState<string>(null);
  const [videoEdit, setVideoEdit] = useState<any>(null);

  const [formData, setFormData] = useState<any>({});
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [fetchGuid, setFetchGuid] = useState('');
  const [videoLoader, setVideoLoader] = useState(false);
  const [tagLoading, setTagLoading] = useState(false);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [authorsLoading, setAuthorsLoading] = useState(false);

  useEffect(() => {
    if (!videoEditId) setVideoEdit(null);
    else requestVideoContent(videoEditId).then(setVideoEdit);
  }, [videoEditId]);

  useEffect(() => {
    setIsCourseReadOnly(false);
    if (courseId) {
      setLoading(true);
      requestCourseContent(courseId).then((course) => {
        setCourse(course);
        if ((course.completions || 0) > 0 || course.visible) {
          setIsCourseReadOnly(true);
        }
        setLoading(false);
        setEditCourseModal(true);
      });
    }
  }, [courseId]);

  const [videoFilter, setVideoFilter] = useState('');

  const [videoProblems, setVideoProblems] = useState<any>({});

  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const unwrap = (doc) => ({ id: doc.id, ...doc.data() });

  const loadTags = async () => {
    const snapshot = await database.collection('tags').orderBy('label', 'asc').get();
    setTags(snapshot.docs.map(unwrap));
  };

  const loadAuthors = async () => {
    setAuthorsLoading(true);
    try {
      const snapshot = await database.collection('authors').orderBy('orderInList', 'asc').get();
      const resourcesBaseUrl = getPublicResourcesBaseUrl();
      setAuthors(
        snapshot.docs.map((doc) => ({
          ...unwrap(doc),
          photo: `${resourcesBaseUrl}authors_data/${doc.id}/avatar.png`,
        }))
      );
    } finally {
      setAuthorsLoading(false);
    }
  };

  const loadFaqs = async () => {
    const snapshot = await database.collection('system/staticData/faqs').orderBy('order', 'asc').get();
    setFaqs(snapshot.docs.map(unwrap));
  };

  const loadCourses = async () => {
    setCoursesLoading(true);
    try {
      const snapshot = await database.collection('courses').get();
      const rawCourses = snapshot.docs.map(unwrap);
      const companyRefs = rawCourses
        .flatMap((course) => course.companies || [])
        .filter(Boolean)
        .reduce((acc, ref) => {
          if (!acc.find((existing) => existing.path === ref.path)) acc.push(ref);
          return acc;
        }, [] as any[]);

      const companyDocs = await Promise.all(companyRefs.map((ref) => ref.get()));
      const companiesByPath = new Map(companyDocs.filter((doc) => doc.exists).map((doc) => [doc.ref.path, unwrap(doc)]));

      const lightCourses = rawCourses.map((course) => ({
        ...course,
        companies: (course.companies || []).map((ref: any) => companiesByPath.get(ref.path)).filter(Boolean),
      }));

      setCourses(lightCourses);
    } finally {
      setCoursesLoading(false);
    }
  };

  const loadVideosByIds = async (ids: string[] = []) => {
    if (!ids.length) {
      setVideos([]);
      return;
    }

    const docs = await Promise.all(ids.map((id) => database.doc(`videos/${id}`).get()));
    const items = docs.filter((doc) => doc.exists).map((d) => {
      const { service, visible, visibleFor = {}, corsoAso, tags = [], languages = ['it'] } = d.data();
      return { id: d.id, service, visible, visibleFor, corsoAso, tags: tags.length, languages };
    });
    setVideos(items);
  };

  const fetchDynamicContent = (forceReload: boolean = false) => {
    const ids = fetchGuid.length ? [fetchGuid] : [];
    if (ids.length) {
      setVideoLoader(true);
      loadVideosByIds(ids).then(() => {
        setVideoLoader(false);
        setFetchLoading(false);
      }).catch(console.error);
    }
    if (activeTab === 'tags' && (!tags.length || forceReload)) {
      if (forceReload) {
        setShowSuccessModal(true);
        setTags([...tags]);
        setShowSuccessModal(false);
      } else {
        setTagLoading(true);
        loadTags().then(() => {
          setTags((prevTags) => [...prevTags]);
          setTagLoading(false);
        }).catch(console.error);
      }
    }
  };

  const fetchInitialContent = () => {
    setLoading(true);
    Promise.all([loadTags(), loadAuthors()])
      .then(() => {
        setLoading(false);
        setFetchLoading(false);
        setShowSuccessModal(false);
      })
      .catch(console.error);
  };

  useEffect(() => {
    fetchInitialContent();
  }, [])

  useEffect(() => {
    fetchDynamicContent();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'courses' && !courses.length && !coursesLoading) loadCourses().catch(console.error);
    if (activeTab === 'faq' && !faqs.length) loadFaqs().catch(console.error);
  }, [activeTab]);

  const filterVideos = () => {
    const filteredVideos = (() => {
      switch (videoFilter) {
        default:
          return (videos || []).filter((video) => (video.title || '').toLowerCase().includes(videoFilter) || video.id.toLowerCase().includes(videoFilter));
      }
    })();

    setFilteredVideos(filteredVideos);
  };

  const filterCourses = () => {
    const filteredCourses = (() => {
      return (courses || []).filter((course) => (course.name || '').toLowerCase().includes(coursesFilter) || course.id.toLowerCase().includes(coursesFilter));
    })();

    setFilteredCourses(filteredCourses);
  };

  useEffect(() => {
    filterVideos();
  }, [videoFilter, videos]);

  useEffect(() => {
    filterCourses();
  }, [coursesFilter, courses]);

  if (loading)
    return (
      <>
        <FullscreenLoader />

        <SeoUpdateInProgress />
      </>
    );

  return (
    <>
      <Title>Gestione contenuti</Title>
      <Topbar />

      <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} backdrop={'static'} keyboard={false}>
        <Alert variant={'success'} className={'mb-0'}>
          Operazione completata con successo! Attendi&hellip;
        </Alert>
      </Modal>

      <Container className={'my-5'}>
        <div className={'display-4'}>Gestione contenuti</div>
        <Tabs activeKey={activeTab} onSelect={setActiveTab} className={'mt-3'} mountOnEnter={true} unmountOnExit={true}>
          <Tab
            eventKey={'help'}
            title={
              activeTab == 'help' ? (
                'Guida'
              ) : (
                <WithTooltip text={'Guida'} big={false}>
                  <FontAwesomeIcon icon={faQuestionCircle} fixedWidth={true} />
                </WithTooltip>
              )
            }
          >
            <div className={'border border-top-0 rounded-bottom px-3 pt-3'}>
              <p>
                Tramite questa sezione, un Amministratore può gestire i contenuti presenti in piattaforma
                <br />
                Le modifiche apportate sono immediatamente visibili agli utenti, quindi <strong>si prega di fare attenzione</strong>.<br />
                In nessuna sezione è possibile eliminare elementi: per effettuare operazioni di eliminazione è necessario rivolgersi al gestore del database.
              </p>
              <p>
                <strong>Tutte le operazioni effettuate in questa sezione sono registrate.</strong>
              </p>
              <h5>
                <a
                  href={'#'}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab('videos');
                  }}
                >
                  <FontAwesomeIcon icon={faVideo} fixedWidth={true} className={'mr-2'} />
                  <strong>Video</strong>
                </a>
              </h5>
              <p>
                Dalla sezione video è possibile nascondere e mostrare video già presenti in piattaforma, oltre che modificare i metadati di ognuno di essi
                (titolo, thumbnail, trailer, autore, descrizione, tags, timestamps, strumentazione, ...).
              </p>
              <h5>
                <a
                  href={'#'}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab('tags');
                  }}
                >
                  <FontAwesomeIcon icon={faTags} fixedWidth={true} className={'mr-2'} />
                  <strong>Tags</strong>
                </a>
              </h5>
              <p>
                Dalla sezione tags si possono creare nuovi tag e rinominare quelli esistenti, oltre che impostarli come serie e cambiare il loro ordine di
                priorità.
              </p>
              <h5>
                <a
                  href={'#'}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab('authors');
                  }}
                >
                  <FontAwesomeIcon icon={faGraduationCap} fixedWidth={true} className={'mr-2'} />
                  <strong>Autori</strong>
                </a>
              </h5>
              <p>Dalla sezione autori si possono creare nuovi autori e rinominare quelli esistenti.</p>
              <h5>
                <a
                  href={'#'}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab('faq');
                  }}
                >
                  <FontAwesomeIcon icon={faQuoteRight} fixedWidth={true} className={'mr-2'} />
                  <strong>FAQ</strong>
                </a>
              </h5>
              <p>Dalla sezione FAQ si possono creare e modificare la FAQ.</p>
              <h5>
                <a
                  href={'#'}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab('series');
                  }}
                >
                  <FontAwesomeIcon icon={faBookMedical} fixedWidth={true} className={'mr-2'} />
                  <strong>Serie</strong>
                </a>
              </h5>
              <p>Dalla sezione serie è possibile gestire le serie di contenuti.</p>
            </div>
          </Tab>

          <Tab
            eventKey={'courses'}
            title={
              <>
                {activeTab == 'courses' ? (
                  'Corsi'
                ) : (
                  <WithTooltip text={'Corsi'} big={false}>
                    <FontAwesomeIcon icon={faCertificate} fixedWidth={true} />
                  </WithTooltip>
                )}
              </>
            }
          >
            {(filteredCourses || []).length != (courses || []).length && (
              <Alert style={{ position: 'fixed', bottom: '1rem', right: '2rem', zIndex: 10 }} variant={'danger'} className={'d-flex align-items-center'}>
                <strong>Filtri di ricerca applicati</strong>
                <Button size={'sm'} variant={'secondary'} className={'ml-3'} onClick={() => setCoursesFilter('')}>
                  Ripristina
                </Button>
              </Alert>
            )}
            <div className={'border border-top-0 rounded-bottom p-3'}>
              <Form.Control
                className={'mb-3'}
                placeholder={'Cerca per Id o Nome'}
                value={coursesFilter}
                onInput={(e) => {
                  const filter = (e.target.value || '').toLowerCase();
                  setCoursesFilter(filter);
                }}
              />

              <div className={'d-flex justify-content-end my-3'}>
                <Button
                  variant={'primary'}
                  onClick={() => {
                    setCourse(null);
                    setEditCourseModal(true);
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} fixedWidth={true} className={'mr-2'} />
                  Crea nuovo corso
                </Button>
              </div>
              <div className={`my-3 ${styles.videoGuids}`}>
                {coursesLoading && <FullscreenLoader fullscreen={false} className="d-flex justify-content-center" />}
                {!filteredCourses.length && <div>Nessun corso trovato</div>}
                {!coursesLoading && filteredCourses.map((course) => (
                  <Card
                    bg={'dark'}
                    key={course.id}
                    onClick={() => {
                      setCourseId(course.id);
                    }}
                  >
                    <div className={'d-flex align-items-start'}>
                      <div className={'w-100'}>
                        <div>
                          <Copyable text={course.id} />
                        </div>
                        <div>
                          <strong>{course.name}</strong>
                        </div>
                        {course.description && course.description.map((description, index) => <div key={index.toString()}>{description}</div>)}
                        {course.order && (
                          <div>
                            <strong>Ordine:</strong> {course.order}
                          </div>
                        )}
                        {typeof course.units === 'number' && (
                          <div>
                            <strong>Unità:</strong> {course.units}
                          </div>
                        )}
                        {typeof course.tests === 'number' && (
                          <div>
                            <strong>Test:</strong> {course.tests}
                          </div>
                        )}
                        {typeof course.completions === 'number' && (
                          <div>
                            {course.completions > 0 ? <strong>Completato da {course.completions} utenti</strong> : <strong>Nessun utente ha completato questo corso</strong>}
                          </div>
                        )}
                      </div>
                      <div className={'ml-2'} style={{ whiteSpace: 'nowrap' }}>
                        <div>
                          {course.withCertificate && (
                            <WithTooltip text={'Con certificato'} big={false}>
                              <Badge variant={'success'}>
                                <FontAwesomeIcon icon={faGraduationCap} fixedWidth={true} />
                              </Badge>
                            </WithTooltip>
                          )}
                          {course.visibleFor &&
                            course.visibleFor.map((item, index) => {
                              if (item.categoriaProfessionale == 'odontoiatra') {
                                return (
                                  <WithTooltip text={'Odontoiatra'} key={index.toString()} big={false}>
                                    <Badge variant={'success'} className={'ml-1'}>
                                      <FontAwesomeIcon icon={faTooth} fixedWidth={true} />
                                    </Badge>
                                  </WithTooltip>
                                );
                              } else if (item.categoriaProfessionale == 'aso') {
                                return (
                                  <WithTooltip text={'ASO'} key={index.toString()} big={false}>
                                    <Badge variant={'success'} className={'ml-1'}>
                                      <FontAwesomeIcon icon={faHandHoldingMedical} fixedWidth={true} />
                                    </Badge>
                                  </WithTooltip>
                                );
                              }
                            })}
                          {course.companies && course.companies.length > 0 && (
                            <WithTooltip text={`${course.companies[0].name}`} big={false}>
                              <Badge variant={'success'} className={'ml-1'}>
                                <FontAwesomeIcon icon={faHandshake} fixedWidth={true} />
                              </Badge>
                            </WithTooltip>
                          )}
                          <WithTooltip text={course.visible ? 'Visibile' : 'Nascosto'} big={false}>
                            <Badge variant={course.visible ? 'success' : 'secondary'} className={'ml-1'}>
                              <FontAwesomeIcon icon={course.visible ? faEye : faEyeSlash} fixedWidth={true} />
                            </Badge>
                          </WithTooltip>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </Tab>

          <Tab
            eventKey={'videos'}
            title={
              <>
                {activeTab == 'videos' ? (
                  'Video'
                ) : (
                  <WithTooltip text={'Video'} big={false}>
                    <FontAwesomeIcon icon={faVideo} fixedWidth={true} />
                  </WithTooltip>
                )}
                {videoProblems.contentWithProblems && (
                  <Badge variant={'warning'} pill className={'ml-2'}>
                    <FontAwesomeIcon icon={faExclamation} size={'sm'} />
                  </Badge>
                )}
              </>
            }
          >
            {(filteredVideos || []).length != (videos || []).length && (
              <Alert style={{ position: 'fixed', bottom: '1rem', right: '2rem', zIndex: 10 }} variant={'danger'} className={'d-flex align-items-center'}>
                <strong>Filtri di ricerca applicati</strong>
                <Button size={'sm'} variant={'secondary'} className={'ml-3'} onClick={() => setVideoFilter('')}>
                  Ripristina
                </Button>
              </Alert>
            )}
            <div className={'border border-top-0 rounded-bottom p-3'}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                <InputGroup size={'lg'} style={{ width: '100%', maxWidth: '750px' }}>
                  <InputGroup.Prepend>
                    <Button variant={!!formData?.json ? 'success' : 'danger'} style={{ pointerEvents: 'none' }}>
                      GUID:
                    </Button>
                  </InputGroup.Prepend>
                  <Form.Control
                    isValid={!!formData?.json}
                    isInvalid={!formData?.json && fetchError}
                    placeholder={'Inserisci il GUID del video...'}
                    disabled={fetchLoading || formData?.json}
                    value={fetchGuid}
                    onKeyDown={(e) => {
                      if (e.key == 'Enter') {
                        e.preventDefault();
                        if (fetchLoading || !fetchGuid || !/^\s*[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\s*$/.test(fetchGuid)) return false;
                        (document.querySelector('#guid-submit-button') as HTMLButtonElement)?.click();
                      }
                    }}
                    onChange={(e) => {
                      setFetchError(false);
                      setFetchGuid(e.target.value.trim());
                    }}
                  />
                  {!formData?.json && (
                    <InputGroup.Append>
                      <Button
                        disabled={fetchLoading || !fetchGuid || !/^\s*[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\s*$/.test(fetchGuid)}
                        variant={'primary'}
                        id={'guid-submit-button'}
                        onClick={() => {
                          setFetchLoading(true);
                          setFetchError(false);
                          fetchDynamicContent();
                        }}
                      >
                        {fetchLoading ? <>Attendi&hellip;</> : <>Ottieni dati&hellip;</>}
                      </Button>
                    </InputGroup.Append>
                  )}
                </InputGroup>
              </div>
              {!!fetchGuid && !/^\s*[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\s*$/.test(fetchGuid) && (
                <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                  <Form.Text style={{ display: 'flex', justifyContent: 'center' }} className={'text-warning'}>
                    Il GUID inserito non è nel formato corretto.
                  </Form.Text>
                </div>
              )}
              {videoLoader && <FullscreenLoader fullscreen={false} className="d-flex justify-content-center" />}
              {!videoLoader && !filteredVideos.length && <div style={{ textAlign: 'center' }}>Nessun risultato.</div>}
              <div className={`my-3 ${styles.videoGuids}`}>
                {!videoLoader && filteredVideos.map((video) => (
                  <Card bg={'dark'} key={video.id} onClick={() => setVideoEditId(video.id)}>
                    <div className={'d-flex align-items-center'}>
                      <div className={'w-100'}>
                        <div>
                          <code>{video.id}</code>
                        </div>
                        <div>
                          {video.languages.map((lang) => (
                            <CountryFlag language={lang} key={lang} withTooltip={true} className={'mr-1'} />
                          ))}
                        </div>
                      </div>
                      <div className={'ml-2'} style={{ whiteSpace: 'nowrap' }}>
                        <div>
                          <Badge variant={video.corsoAso ? 'danger' : 'secondary'}>
                            <FontAwesomeIcon icon={faGraduationCap} fixedWidth={true} />
                          </Badge>
                          <Badge variant={'primary'} className={'ml-1'}>
                            <FontAwesomeIcon icon={faTag} fixedWidth={true} /> {video.tags}
                          </Badge>
                        </div>
                        <div>
                          <Badge variant={video.visibleFor.odontoiatra ? 'success' : 'secondary'}>
                            <FontAwesomeIcon icon={faTooth} fixedWidth={true} />
                          </Badge>
                          <Badge variant={video.visibleFor.aso ? 'success' : 'secondary'} className={'ml-1'}>
                            <FontAwesomeIcon icon={faHandHoldingMedical} fixedWidth={true} />
                          </Badge>
                          <Badge variant={video.visible ? 'success' : 'secondary'} className={'ml-1'}>
                            <FontAwesomeIcon icon={video.visible ? faEye : faEyeSlash} fixedWidth={true} />
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
                {/*{filteredVideos.map((video) => (
                  <AdminVideo
                    key={video.id}
                    video={video}
                    setLoading={setLoading}
                    onUpdate={() => {
                      setShowSuccessModal(true);
                      setLoading(true);
                      upsertHubspotProduct(video.id, 'video')
                        .then(() => manageCache('DEL_REDIS_KEY', 'videos_admin')())
                        .then(() => fetchContent())
                        .catch(console.error);
                    }}
                    authors={authors}
                    tags={tags}
                  />
                ))}*/}
              </div>
            </div>
          </Tab>

          <Tab
            eventKey={'tags'}
            title={
              activeTab == 'tags' ? (
                'Tags'
              ) : (
                <WithTooltip text={'Tags'} big={false}>
                  <FontAwesomeIcon icon={faTags} fixedWidth={true} />
                </WithTooltip>
              )
            }
          >
            <div className={'border border-top-0 rounded-bottom p-3'} style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gridGap: '0.75rem' }}>
              <AdminCreateTag
                setLoading={setLoading}
                onCreate={(createdTag) => {
                  // Add the new tag to the existing tags array
                  setTags(prevTags => [...prevTags, createdTag]);
                  setShowSuccessModal(true);
                  setTimeout(() => setShowSuccessModal(false), 2000);
                }}
              />
              {tagLoading && <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center' }}><FullscreenLoader fullscreen={false} /></div>}
              {!tagLoading && (tags || []).map((tag, index) => (
                <AdminTag
                  key={`${tag.id}-${index}`}
                  tag={tag}
                  setLoading={setLoading}
                  onUpdate={(updatedTag) => {
                    setTags(prevTags =>
                      prevTags.map(t => t.id === updatedTag.id ? updatedTag : t)
                    );
                    setShowSuccessModal(true);
                    setTimeout(() => setShowSuccessModal(false), 1000);
                  }}
                />
              ))}
            </div>
          </Tab>

          <Tab
            eventKey={'authors'}
            title={
              activeTab == 'authors' ? (
                'Autori'
              ) : (
                <WithTooltip text={'Autori'} big={false}>
                  <FontAwesomeIcon icon={faGraduationCap} fixedWidth={true} />
                </WithTooltip>
              )
            }
          >
            <div className={'border border-top-0 rounded-bottom p-3'} style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gridGap: '0.75rem' }}>
              <AdminCreateAuthor
                setLoading={setLoading}
                onCreate={() => {
                  setShowSuccessModal(true);
                  loadAuthors().catch(console.error);
                }}
              />
              {authorsLoading && <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center' }}><FullscreenLoader fullscreen={false} /></div>}
              {(authors || []).map((author) => (
                <AdminAuthor
                  key={author.id}
                  author={author}
                  onUpdate={() => {
                    setShowSuccessModal(true);
                    loadAuthors().catch(console.error);
                  }}
                />
              ))}
            </div>
          </Tab>

          <Tab
            eventKey={'faq'}
            title={
              activeTab == 'faq' ? (
                'FAQ'
              ) : (
                <WithTooltip text={'FAQ'} big={false}>
                  <FontAwesomeIcon icon={faQuoteRight} fixedWidth={true} />
                </WithTooltip>
              )
            }
          >
            <div className={'border border-top-0 rounded-bottom p-3'}>
              <Wip />
              {/*<AdminCreateFaq />*/}
              {/*<AdminFaqs faqs={faqs} />*/}
            </div>
          </Tab>

          <Tab
            eventKey={'series'}
            title={
              activeTab == 'series' ? (
                'Serie'
              ) : (
                <WithTooltip text={'Serie'} big={false}>
                  <FontAwesomeIcon icon={faBookMedical} fixedWidth={true} />
                </WithTooltip>
              )
            }
          >
            <div className={'border border-top-0 rounded-bottom p-3'}>
              <Wip />
            </div>
          </Tab>
        </Tabs>
      </Container>
      <Footer />
      {!!videoEditId && (
        <Modal
          size={!!videoEdit ? 'xl' : null}
          className={'dark-modal'}
          centered
          show={!!videoEditId}
          onHide={() => setVideoEditId(null)}
          backdrop={'static'}
          keyboard={false}
        >
          <Modal.Body className={!!videoEdit ? 'pb-0' : null}>
            {!videoEdit && <div className={'text-center'}>Caricamento&hellip;</div>}
            {!!videoEdit && (
              <AdminVideo
                video={videoEdit}
                setLoading={setLoading}
                onUpdate={() => {
                  setShowSuccessModal(true);
                  setLoading(true);
                  upsertHubspotProduct(videoEditId, 'video')
                    .then(() => manageCache('DEL_REDIS_KEY', 'videos_admin')())
                    .then(() => setVideoEditId(null))
                    .then(() => loadVideosByIds(fetchGuid.length ? [fetchGuid] : []))
                    .then(() => setLoading(false))
                    .catch(console.error);
                }}
                authors={authors}
                tags={tags}
              />
            )}
          </Modal.Body>
          {!!videoEdit && (
            <Modal.Footer>
              <Button variant={'secondary'} onClick={() => setVideoEditId(null)}>
                Chiudi
              </Button>
            </Modal.Footer>
          )}
        </Modal>
      )}

      <EditCourse
        course={course}
        loading={loading}
        editCourseModal={editCourseModal}
        setEditCourseModal={setEditCourseModal}
        setLoading={setLoading}
        fetchContent={fetchInitialContent}
        setCourseId={setCourseId}
        isCourseReadOnly={isCourseReadOnly}
      />

      <SeoUpdateInProgress />
    </>
  );
};

export default ContentComponent;
